import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Input,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import type { ApexOptions } from "apexcharts";
import type ApexCharts from "apexcharts";
import SafeApexChart from "../../Components/Common/SafeApexChart";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SendApprovalRejectModal from "../../Components/Common/SendApprovalRejectModal";
import {
  getSendingSetup,
  SendingSetup,
} from "../../helpers/sending_setup_helper";
import {
  effectiveQuota,
  getSubscription,
  getSubscriptionUsageSeries,
  QUOTA_PACK_EMAILS,
  QUOTA_PACK_MAX_PER_PERIOD,
  QUOTA_PACK_PRICE_CENTS,
  Subscription,
  SubscriptionUsageSeries,
} from "../../helpers/subscription_helper";
import {
  formatCents,
  formatCentsPerThousand,
  intlLocaleFromI18n,
} from "../../helpers/money";
import {
  approveCampaignSend,
  BestOpenHours,
  CampaignSend,
  formatRatePercent,
  getBestOpenHours,
  getPendingApprovals,
  getSends,
  getSendStats,
  getSendStatsSeries,
  isBounceRateHigh,
  clampBounceWarnThreshold,
  BOUNCE_RATE_WARN_THRESHOLD,
  rejectCampaignSend,
  SendStats,
  SendStatsSeries,
} from "../../helpers/sends_helper";
import { Campaign, getCampaigns } from "../../helpers/campaigns_helper";
import { fetchApiMessageHistory } from "../../helpers/api_messages_helper";
import { fetchMe, type MeTenant } from "../../helpers/profile_helper";
import { mergeTenantMeIntoSession } from "../../helpers/api_helper";
import {
  formatNextUtcDayResetLocal,
  readDisplayTimezone,
} from "../../helpers/tenantTimezone";
import {
  getInactiveContactsSummary,
  getSegments,
  InactiveContactsSummary,
  updateInactiveListSettings,
} from "../../helpers/audience_helper";
import {
  ByoCircuitSettings,
  getByoCircuitSettings,
} from "../../helpers/byo_circuit_helper";
import {
  getWelcomeAutomation,
  getWinBackAutomation,
} from "../../helpers/automations_helper";
import { getForms } from "../../helpers/forms_helper";
import { canApproveSend, isOwner } from "../../utils/tenantRoles";
import {
  formatCircuitAutoResumeCountdown,
  hasCircuitAutoResumeEta,
} from "../../helpers/circuitAutoResumeEta";
import { useCircuitAutoResumeNowMs } from "../../helpers/useCircuitAutoResumeNowMs";
import {
  buildAttentionItems,
  buildDiscoveryCards,
  formatRelativePast,
  resolveNextStep,
  sendStatusBadgeClass,
  type AttentionItem,
} from "./dashboardHomeLogic";
import DashboardTour from "./DashboardTour";
import {
  compareSeriesHalves,
  downloadTextFile,
  isDashboardTourDone,
  readDashboardPrefs,
  readLastCampaignEditId,
  seriesToCsv,
  shouldShowBounceWorsenToast,
  writeDashboardPrefs,
  type DashboardHomePrefs,
  type SeriesMetricMode,
} from "./dashboardPrefs";

type StatKpiProps = {
  label: string;
  value: string;
  icon: string;
  hint?: string;
  /** danger/warning: destaca KPI (ex.: bounce alto) sem esconder o número. */
  tone?: "default" | "danger" | "warning";
  /** Mini tendência (ex.: bounce diário) — só visual. */
  sparkSeries?: number[];
};

const StatKpiCard = ({
  label,
  value,
  icon,
  hint,
  tone = "default",
  sparkSeries,
}: StatKpiProps) => {
  const iconClass =
    tone === "danger"
      ? "bg-danger-subtle text-danger"
      : tone === "warning"
        ? "bg-warning-subtle text-warning"
        : "bg-primary-subtle text-primary";
  const valueClass =
    tone === "danger"
      ? "text-danger"
      : tone === "warning"
        ? "text-warning"
        : "";
  const cardClass =
    tone === "danger"
      ? "border border-danger border-opacity-25"
      : tone === "warning"
        ? "border border-warning border-opacity-25"
        : "";
  const sparkColor =
    tone === "danger" ? "#f06548" : tone === "warning" ? "#f7b84b" : "#b2d608";
  const sparkOpts: ApexOptions | undefined =
    sparkSeries && sparkSeries.length >= 2
      ? {
          chart: {
            type: "area",
            sparkline: { enabled: true },
            toolbar: { show: false },
          },
          stroke: { curve: "smooth", width: 2 },
          fill: { opacity: 0.2 },
          colors: [sparkColor],
          tooltip: { enabled: false },
        }
      : undefined;

  return (
    <Card className={`card-animate mb-0 h-100 ${cardClass}`.trim()}>
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-0">{label}</p>
            <h4 className={`fs-22 fw-semibold mb-0 mt-2 ${valueClass}`.trim()}>
              {value}
            </h4>
            {hint ? <p className="text-muted small mb-0 mt-1">{hint}</p> : null}
            {sparkOpts && sparkSeries ? (
              <div className="mt-2" aria-hidden="true">
                <SafeApexChart
                  options={sparkOpts}
                  series={[{ name: "spark", data: sparkSeries }]}
                  type="area"
                  height={36}
                />
              </div>
            ) : null}
          </div>
          <div className="avatar-sm flex-shrink-0">
            <span
              className={`avatar-title ${iconClass} rounded-circle fs-3`}
            >
              <i className={icon} />
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

/** Toggle da home: ativo = lima + preto (marca); inativo = cinza claro. */
const dashSegBtn = (active: boolean) =>
  `btn btn-sm ${active ? "btn-primary" : "btn-light"}`;

const THRESHOLD_OPTIONS = [10, 15, 20, 25, 30, 40, 50, 60, 70, 80];

/**
 * Home tenant — centro de comando (próxima ação + saúde + métricas).
 * Por quê: o produto cresceu; atalhos estáticos não respondem “o que fazer agora”.
 */
const TenantHome = () => {
  const { t, i18n } = useTranslation();
  document.title = t("dashboard.documentTitle");

  const [setup, setSetup] = useState<SendingSetup | null>(null);
  const [setupLoading, setSetupLoading] = useState(true);

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quotaUnavailable, setQuotaUnavailable] = useState(false);
  const [usageSeries, setUsageSeries] =
    useState<SubscriptionUsageSeries | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [recentSends, setRecentSends] = useState<CampaignSend[]>([]);
  const [sendsLoading, setSendsLoading] = useState(true);
  const recentEtaActive = useMemo(
    () => recentSends.some((row) => hasCircuitAutoResumeEta(row)),
    [recentSends]
  );
  const etaNowMs = useCircuitAutoResumeNowMs(recentEtaActive);

  const [stats, setStats] = useState<SendStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [byoCircuit, setByoCircuit] = useState<ByoCircuitSettings | null>(null);
  const [series, setSeries] = useState<SendStatsSeries | null>(null);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState(false);
  const [seriesDays, setSeriesDays] = useState<7 | 30>(30);
  // Prefs no localStorage — métrica e “ocultar sugestões” sem round-trip.
  const [prefs, setPrefs] = useState<DashboardHomePrefs>(() =>
    readDashboardPrefs()
  );
  const [seriesMetric, setSeriesMetric] = useState<SeriesMetricMode>(
    () => readDashboardPrefs().seriesMetric
  );
  // Série 14d só para delta semana atual vs anterior no digest.
  const [compareSeries, setCompareSeries] = useState<SendStatsSeries | null>(
    null
  );
  const [bestHours, setBestHours] = useState<BestOpenHours | null>(null);
  const [winBackEnabled, setWinBackEnabled] = useState<boolean | null>(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [lastCampaignEditId] = useState<number | null>(() =>
    readLastCampaignEditId()
  );
  // chartRef (react-apexcharts v2) = instância ApexCharts — PNG via dataURI().
  const seriesChartRef = useRef<ApexCharts | null>(null);
  const [inactiveSummary, setInactiveSummary] =
    useState<InactiveContactsSummary | null>(null);
  const [apiFailedToday, setApiFailedToday] = useState(0);
  const [apiFailedTopToken, setApiFailedTopToken] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [ramp, setRamp] = useState<MeTenant["sending_ramp"] | null>(null);
  const [rampLoading, setRampLoading] = useState(true);
  const [prepareSendCampaignId, setPrepareSendCampaignId] = useState<
    number | null
  >(null);
  const [campaignsTotal, setCampaignsTotal] = useState<number | null>(null);
  const [campaignWithoutHtml, setCampaignWithoutHtml] =
    useState<Campaign | null>(null);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [segmentsTotal, setSegmentsTotal] = useState<number | null>(null);
  const [formsTotal, setFormsTotal] = useState<number | null>(null);
  const [welcomeEnabled, setWelcomeEnabled] = useState<boolean | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(
    () =>
      typeof localStorage !== "undefined" &&
      localStorage.getItem("mfa_enable_banner_dismissed") === "1"
  );
  const [dismissedAttentionIds, setDismissedAttentionIds] = useState<
    string[]
  >([]);
  // Mobile: KPIs 7d começam fechados — menos scroll no primeiro viewport.
  const [weekStatsOpen, setWeekStatsOpen] = useState(false);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<CampaignSend | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  let tenantName = t("dashboard.defaultTenantName");
  try {
    const raw = sessionStorage.getItem("authUser");
    if (raw) {
      const parsed = JSON.parse(raw);
      tenantName = parsed?.tenant?.name || parsed?.user?.name || tenantName;
    }
  } catch {
    // ignore
  }

  const loadSetup = useCallback(async () => {
    setSetupLoading(true);
    try {
      const res = await getSendingSetup();
      if (res?.success) {
        setSetup(res.data);
      }
    } catch {
      setSetup(null);
    } finally {
      setSetupLoading(false);
    }
  }, []);

  const loadByoCircuit = useCallback(async () => {
    if (!isOwner()) {
      setByoCircuit(null);
      return;
    }
    try {
      const res = await getByoCircuitSettings();
      if (res?.success && res.data?.available) {
        setByoCircuit(res.data);
      } else {
        setByoCircuit(null);
      }
    } catch {
      setByoCircuit(null);
    }
  }, []);

  const loadSubscription = useCallback(async () => {
    setQuotaLoading(true);
    setQuotaUnavailable(false);
    try {
      const res = await getSubscription();
      if (res?.success && res.data?.plan) {
        setSubscription(res.data);
      } else {
        setSubscription(null);
        setQuotaUnavailable(true);
      }
    } catch {
      setSubscription(null);
      setQuotaUnavailable(true);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  // Série de cota no ciclo (owner) — LAC-020.
  const loadUsageSeries = useCallback(async () => {
    if (!isOwner()) {
      setUsageSeries(null);
      return;
    }
    setUsageLoading(true);
    try {
      const res = await getSubscriptionUsageSeries();
      if (res?.success && res.data) {
        setUsageSeries(res.data);
      } else {
        setUsageSeries(null);
      }
    } catch {
      setUsageSeries(null);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  const loadRecentSends = useCallback(async () => {
    setSendsLoading(true);
    try {
      const res = await getSends({ page: 1, per_page: 5 });
      if (res?.success) {
        setRecentSends(res.data || []);
      } else {
        setRecentSends([]);
      }
    } catch {
      setRecentSends([]);
    } finally {
      setSendsLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(false);
    try {
      const res = await getSendStats({ days: 7 });
      if (res?.success && res.data) {
        setStats(res.data);
      } else {
        setStats(null);
        setStatsError(true);
      }
    } catch {
      setStats(null);
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadSeries = useCallback(async () => {
    setSeriesLoading(true);
    setSeriesError(false);
    try {
      const res = await getSendStatsSeries({ days: seriesDays });
      if (res?.success && res.data) {
        setSeries(res.data);
      } else {
        setSeries(null);
        setSeriesError(true);
      }
    } catch {
      setSeries(null);
      setSeriesError(true);
    } finally {
      setSeriesLoading(false);
    }
  }, [seriesDays]);

  // 14d fixo — metade atual vs anterior no digest (independente do gráfico 7/30).
  const loadCompareSeries = useCallback(async () => {
    try {
      const res = await getSendStatsSeries({ days: 14 });
      if (res?.success && res.data) {
        setCompareSeries(res.data);
      } else {
        setCompareSeries(null);
      }
    } catch {
      setCompareSeries(null);
    }
  }, []);

  const loadBestHours = useCallback(async () => {
    try {
      const res = await getBestOpenHours({ days: 30 });
      if (res?.success && res.data) {
        setBestHours(res.data);
      } else {
        setBestHours(null);
      }
    } catch {
      setBestHours(null);
    }
  }, []);

  const loadRamp = useCallback(async () => {
    setRampLoading(true);
    try {
      const me = await fetchMe();
      mergeTenantMeIntoSession(me);
      setRamp(me.tenant?.sending_ramp ?? null);
      setMfaEnabled(Boolean(me.user?.mfa_enabled));
      if (me.user?.mfa_enabled) {
        localStorage.setItem("mfa_enable_banner_dismissed", "1");
        setMfaBannerDismissed(true);
      }
    } catch {
      setRamp(null);
      setMfaEnabled(null);
    } finally {
      setRampLoading(false);
    }
  }, []);

  const loadInactiveSummary = useCallback(async () => {
    try {
      const res = await getInactiveContactsSummary({ days: 90 });
      if (res?.success && res.data) {
        setInactiveSummary(res.data);
      } else {
        setInactiveSummary(null);
      }
    } catch {
      setInactiveSummary(null);
    }
  }, []);

  const loadApiFailedToday = useCallback(async () => {
    try {
      const res = await fetchApiMessageHistory({
        source: "api",
        page: 1,
        per_page: 1,
        status: "failed",
      });
      setApiFailedToday(res.data.meta.failed_today ?? 0);
      const top = res.data.meta.top_failed_token;
      setApiFailedTopToken(
        top && top.id
          ? { id: top.id, name: top.name }
          : null
      );
    } catch {
      setApiFailedToday(0);
      setApiFailedTopToken(null);
    }
  }, []);

  // Campanhas + discovery F2 — leves (per_page=1) só para total / empty state.
  const loadContextHints = useCallback(async () => {
    try {
      const [campaignsRes, emptyHtmlRes] = await Promise.all([
        getCampaigns({ page: 1, per_page: 1 }),
        getCampaigns({ page: 1, per_page: 1, has_html: false }),
      ]);
      if (campaignsRes?.success) {
        setCampaignsTotal(campaignsRes.meta?.total ?? 0);
        const first = campaignsRes.data?.[0] ?? null;
        setPrepareSendCampaignId(first?.id ?? null);
      } else {
        setCampaignsTotal(null);
      }
      if (emptyHtmlRes?.success && (emptyHtmlRes.meta?.total ?? 0) > 0) {
        setCampaignWithoutHtml(emptyHtmlRes.data?.[0] ?? null);
      } else {
        setCampaignWithoutHtml(null);
      }
    } catch {
      setCampaignsTotal(null);
      setCampaignWithoutHtml(null);
    }

    if (canApproveSend()) {
      try {
        const res = await getPendingApprovals({ page: 1, per_page: 1 });
        setPendingApprovalCount(res.meta?.total ?? 0);
      } catch {
        setPendingApprovalCount(0);
      }
    } else {
      setPendingApprovalCount(0);
    }

    try {
      const [segRes, formsRes, welcomeRes, winBackRes] = await Promise.all([
        getSegments({ page: 1, per_page: 1 }),
        getForms({ page: 1, per_page: 1 }),
        getWelcomeAutomation(),
        getWinBackAutomation(),
      ]);
      setSegmentsTotal(segRes?.success ? (segRes.meta?.total ?? 0) : null);
      setFormsTotal(formsRes?.success ? (formsRes.meta?.total ?? 0) : null);
      setWelcomeEnabled(
        welcomeRes?.success ? Boolean(welcomeRes.data?.enabled) : null
      );
      setWinBackEnabled(
        winBackRes?.success ? Boolean(winBackRes.data?.enabled) : null
      );
    } catch {
      setSegmentsTotal(null);
      setFormsTotal(null);
      setWelcomeEnabled(null);
      setWinBackEnabled(null);
    }
  }, []);

  useEffect(() => {
    loadSetup();
    loadByoCircuit();
    loadSubscription();
    loadUsageSeries();
    loadRecentSends();
    loadStats();
    loadRamp();
    loadInactiveSummary();
    loadApiFailedToday();
    loadContextHints();
    loadCompareSeries();
    loadBestHours();
  }, [
    loadSetup,
    loadByoCircuit,
    loadSubscription,
    loadUsageSeries,
    loadRecentSends,
    loadStats,
    loadRamp,
    loadInactiveSummary,
    loadApiFailedToday,
    loadContextHints,
    loadCompareSeries,
    loadBestHours,
  ]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // Tour 1× após setup pronto — Carla vê o mapa sem ler docs.
  useEffect(() => {
    if (setup?.is_ready && !isDashboardTourDone()) {
      setTourOpen(true);
    }
  }, [setup?.is_ready]);

  const persistSeriesMetric = (mode: SeriesMetricMode) => {
    setSeriesMetric(mode);
    setPrefs(writeDashboardPrefs({ seriesMetric: mode }));
  };

  const persistHideDiscovery = (hide: boolean) => {
    setPrefs(writeDashboardPrefs({ hideDiscovery: hide }));
  };

  const used = subscription?.emails_used_in_period ?? 0;
  const quota = effectiveQuota(subscription);
  const packs = subscription?.quota_packs_in_period ?? 0;
  const remaining = Math.max(quota - used, 0);
  const percent =
    quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const nearLimit = quota > 0 && percent >= 80;
  const exhausted = quota > 0 && used >= quota;
  const canBuyPack =
    subscription?.billing_channel !== "brdominio" &&
    (subscription?.status === "active" ||
      subscription?.status === "past_due") &&
    packs < QUOTA_PACK_MAX_PER_PERIOD &&
    nearLimit;
  const packsExhausted =
    subscription?.billing_channel !== "brdominio" &&
    nearLimit &&
    packs >= QUOTA_PACK_MAX_PER_PERIOD;

  const progressColor = exhausted
    ? "danger"
    : nearLimit
      ? "warning"
      : "success";

  const formatPeriodEnd = (iso: string | null | undefined): string | null => {
    if (!iso) {
      return null;
    }
    try {
      return new Intl.DateTimeFormat(i18n.language || "pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(iso));
    } catch {
      return null;
    }
  };

  const periodEndLabel = formatPeriodEnd(subscription?.current_period_end);
  const locale = intlLocaleFromI18n(i18n.language);
  const planCurrency =
    subscription?.plan?.currency || subscription?.currency || "BRL";
  const planPriceCents =
    typeof subscription?.plan?.base_price_cents === "number"
      ? subscription.plan.base_price_cents
      : null;

  const showDailyCap =
    !rampLoading &&
    ramp != null &&
    ramp.daily_cap != null &&
    (ramp.stage === "new" || ramp.stage === "warming");
  const dailyCap = ramp?.daily_cap ?? 0;
  const dailySent = ramp?.sent_today ?? 0;
  const dailyRemaining = ramp?.remaining_today ?? 0;
  const dailyPercent =
    dailyCap > 0
      ? Math.min(100, Math.round((dailySent / dailyCap) * 100))
      : 0;
  const dailyProgressColor =
    dailyRemaining <= 0 ? "danger" : dailyPercent >= 80 ? "warning" : "primary";
  const dailyCapResetLocal = useMemo(
    () =>
      formatNextUtcDayResetLocal(
        readDisplayTimezone(),
        i18n.language || "pt-BR"
      ),
    [i18n.language]
  );

  const prepareSendHref =
    prepareSendCampaignId != null
      ? `/campaigns/${prepareSendCampaignId}/send`
      : "/campaigns";

  const bounceWarnThreshold = clampBounceWarnThreshold(
    stats?.bounce_rate_warn_threshold_percent ?? BOUNCE_RATE_WARN_THRESHOLD
  );
  const bounceRateHigh = isBounceRateHigh(
    stats?.bounce_rate,
    bounceWarnThreshold
  );
  const hardBounceRateHigh = isBounceRateHigh(
    stats?.hard_bounce_rate,
    bounceWarnThreshold
  );
  const bounceRateLabel = bounceRateHigh
    ? formatRatePercent(stats?.bounce_rate, locale)
    : null;
  const hardBounceRateLabel = hardBounceRateHigh
    ? formatRatePercent(stats?.hard_bounce_rate, locale)
    : null;

  const nextStepInput = useMemo(
    () => ({
      setup,
      setupLoading,
      pendingApprovalCount,
      canApprove: canApproveSend(),
      quotaExhausted: exhausted,
      quotaNearLimit: nearLimit && !exhausted,
      showDailyCap,
      dailyRemaining,
      dailyCapResetLocal,
      prepareSendHref,
      inactiveSummary,
      apiFailedToday,
      apiFailedTopTokenName: apiFailedTopToken?.name ?? null,
      apiFailedTopTokenId: apiFailedTopToken?.id ?? null,
      campaignsTotal,
      campaignWithoutHtml,
      recentSends,
      mfaNeeded:
        isOwner() && mfaEnabled === false && !mfaBannerDismissed,
      bounceRateLabel,
      hardBounceRateLabel,
      bounceRateHigh,
      hardBounceRateHigh,
    }),
    [
      setup,
      setupLoading,
      pendingApprovalCount,
      exhausted,
      nearLimit,
      showDailyCap,
      dailyRemaining,
      dailyCapResetLocal,
      prepareSendHref,
      inactiveSummary,
      apiFailedToday,
      apiFailedTopToken,
      campaignsTotal,
      campaignWithoutHtml,
      recentSends,
      mfaEnabled,
      mfaBannerDismissed,
      bounceRateLabel,
      hardBounceRateLabel,
      bounceRateHigh,
      hardBounceRateHigh,
    ]
  );

  const nextStep = useMemo(
    () => resolveNextStep(nextStepInput),
    [nextStepInput]
  );

  const attentionItems = useMemo(() => {
    const raw = buildAttentionItems(nextStepInput, nextStep.id);
    return raw.filter((item) => !dismissedAttentionIds.includes(item.id));
  }, [nextStepInput, nextStep.id, dismissedAttentionIds]);

  const discoveryCards = useMemo(() => {
    if (prefs.hideDiscovery) {
      return [];
    }
    const threshold = inactiveSummary?.dashboard_threshold_percent ?? 20;
    const cold =
      inactiveSummary != null &&
      inactiveSummary.eligible_percent >= threshold;
    return buildDiscoveryCards({
      segmentsTotal,
      formsTotal,
      welcomeEnabled,
      winBackOffAndCold: winBackEnabled === false && cold,
    });
  }, [
    prefs.hideDiscovery,
    inactiveSummary,
    segmentsTotal,
    formsTotal,
    welcomeEnabled,
    winBackEnabled,
  ]);

  const weekCompare = useMemo(
    () => compareSeriesHalves(compareSeries?.series ?? []),
    [compareSeries]
  );

  // Bounce diário (taxa %) p/ sparkline no KPI — só se há volume.
  const bounceSparkSeries = useMemo(() => {
    const points = series?.series ?? [];
    if (points.length < 2) {
      return undefined;
    }
    return points.map((p) =>
      p.sent > 0 ? Math.round((p.bounced / p.sent) * 1000) / 10 : 0
    );
  }, [series]);

  useEffect(() => {
    if (!weekCompare.hasCompare || !bounceRateHigh) {
      return;
    }
    if (!shouldShowBounceWorsenToast(weekCompare.bounceRatePp)) {
      return;
    }
    toast.warning(
      t("dashboard.bounceWorsen.toast", {
        delta: weekCompare.bounceRatePp,
        rate: bounceRateLabel || "",
      }),
      { toastId: "dashboard-bounce-worsen", autoClose: 10000 }
    );
  }, [
    weekCompare.hasCompare,
    weekCompare.bounceRatePp,
    bounceRateHigh,
    bounceRateLabel,
    t,
  ]);

  // Lista aquecida = % fria abaixo do limiar (bom sinal no digest).
  const isHotList =
    inactiveSummary != null &&
    inactiveSummary.eligible_percent <
      (inactiveSummary.dashboard_threshold_percent ?? 20);

  const dismissAttention = (item: AttentionItem) => {
    if (item.id === "mfa") {
      localStorage.setItem("mfa_enable_banner_dismissed", "1");
      setMfaBannerDismissed(true);
    }
    setDismissedAttentionIds((prev) =>
      prev.includes(item.id) ? prev : [...prev, item.id]
    );
  };

  // Skeleton único até o núcleo (setup/cota/ramp/stats) assentar.
  const bootLoading =
    setupLoading || quotaLoading || rampLoading || statsLoading;

  const saveThreshold = async (value: number) => {
    setThresholdSaving(true);
    try {
      const res = await updateInactiveListSettings({
        dashboard_threshold_percent: value,
      });
      if (!res?.success) {
        toast.error(t("dashboard.threshold.error"));
        return;
      }
      setInactiveSummary((prev) =>
        prev
          ? {
              ...prev,
              dashboard_threshold_percent: res.data.dashboard_threshold_percent,
            }
          : prev
      );
      toast.success(t("dashboard.threshold.saved"));
      await loadInactiveSummary();
    } catch {
      toast.error(t("dashboard.threshold.error"));
    } finally {
      setThresholdSaving(false);
    }
  };

  const approveSendInline = async (send: CampaignSend) => {
    setApprovalBusyId(send.id);
    try {
      const res = await approveCampaignSend(send.id);
      if (!res?.success) {
        toast.error(t("sending.approval.error"));
        return;
      }
      toast.success(t("sending.approval.approved"));
      await Promise.all([loadRecentSends(), loadContextHints()]);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: string }).message)
          : t("sending.approval.error");
      toast.error(msg);
    } finally {
      setApprovalBusyId(null);
    }
  };

  const confirmRejectInline = async () => {
    if (!rejectTarget) {
      return;
    }
    setApprovalBusyId(rejectTarget.id);
    try {
      const res = await rejectCampaignSend(
        rejectTarget.id,
        rejectionReason.trim() || undefined
      );
      if (!res?.success) {
        toast.error(t("sending.approval.error"));
        return;
      }
      toast.success(t("sending.approval.rejected"));
      setRejectTarget(null);
      setRejectionReason("");
      await Promise.all([loadRecentSends(), loadContextHints()]);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: string }).message)
          : t("sending.approval.error");
      toast.error(msg);
    } finally {
      setApprovalBusyId(null);
    }
  };

  // Toast 1× por visita quando teto do dia = 0 (LAC-090).
  const dailyCapToastShown = useRef(false);
  useEffect(() => {
    if (!showDailyCap || dailyRemaining > 0 || dailyCapToastShown.current) {
      return;
    }
    dailyCapToastShown.current = true;
    toast.warning(
      <span>
        <Trans
          i18nKey="dashboard.dailyCap.exhausted"
          values={{ resetAt: dailyCapResetLocal }}
          components={{ strong: <strong /> }}
        />{" "}
        <Link
          to="/settings/deliverability"
          className="text-decoration-underline fw-semibold"
        >
          {t("dashboard.dailyCap.exhaustedHubCta")}
        </Link>
        {" · "}
        <Link
          to="/campaigns"
          className="text-decoration-underline fw-semibold"
        >
          {t("dashboard.dailyCap.exhaustedCta")}
        </Link>
      </span>,
      { autoClose: 10000, toastId: "dashboard-daily-cap-exhausted" }
    );
  }, [showDailyCap, dailyRemaining, dailyCapResetLocal, t]);

  const quotaDonutOptions = useMemo((): ApexOptions => {
    const usedLabel = t("dashboard.quota.chart.used");
    const remainingLabel = t("dashboard.quota.chart.remaining");
    const per1kUsed =
      planPriceCents != null && used > 0
        ? formatCentsPerThousand(planPriceCents, used, planCurrency, locale)
        : null;
    const per1kPlan =
      planPriceCents != null && quota > 0
        ? formatCentsPerThousand(planPriceCents, quota, planCurrency, locale)
        : null;

    return {
      chart: { type: "donut", toolbar: { show: false } },
      labels: [usedLabel, remainingLabel],
      colors: [
        exhausted ? "#f06548" : nearLimit ? "#f7b84b" : "#b2d608",
        "#e9ebec",
      ],
      legend: { show: false },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: { show: true, fontSize: "12px" },
              value: {
                show: true,
                fontSize: "16px",
                formatter: () => `${percent}%`,
              },
              total: {
                show: true,
                label: t("dashboard.quota.chart.usedShare"),
                formatter: () => `${percent}%`,
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val: number, opts?: { seriesIndex?: number }) => {
            const idx = opts?.seriesIndex;
            const extra =
              idx === 0 && per1kUsed
                ? ` · ${per1kUsed}/1k`
                : idx === 1 && per1kPlan
                  ? ` · ${t("dashboard.quota.chart.planRate", { amount: per1kPlan })}`
                  : "";
            return `${Number(val).toLocaleString(locale)}${extra}`;
          },
        },
      },
    };
  }, [
    t,
    locale,
    used,
    quota,
    percent,
    exhausted,
    nearLimit,
    planPriceCents,
    planCurrency,
  ]);

  const quotaDonutSeries = useMemo(
    () => [used, Math.max(remaining, 0)],
    [used, remaining]
  );

  const hasWeekVolume = (stats?.sent ?? 0) > 0 || (stats?.sends_count ?? 0) > 0;

  const hasSeriesVolume = useMemo(() => {
    const points = series?.series ?? [];
    if (seriesMetric === "engagement") {
      return points.some((p) => p.opened > 0 || p.clicked > 0);
    }
    if (seriesMetric === "goals") {
      return points.some((p) => p.goal_count > 0);
    }
    return points.some((p) => p.sent > 0 || p.bounced > 0);
  }, [series, seriesMetric]);

  const seriesChartOptions = useMemo((): ApexOptions => {
    const categories = (series?.series ?? []).map((p) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short",
        }).format(new Date(p.date));
      } catch {
        return p.date;
      }
    });
    const colors =
      seriesMetric === "engagement"
        ? ["#0ab39c", "#299cdb"]
        : seriesMetric === "goals"
          ? ["#f7b84b"]
          : ["#b2d608", "#f06548"];
    return {
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { width: 2, curve: "smooth" },
      dataLabels: { enabled: false },
      colors,
      legend: { position: "top" },
      xaxis: { categories },
      yaxis: {
        labels: {
          formatter: (v: number) => Math.round(v).toLocaleString(locale),
        },
      },
      grid: { borderColor: "#f1f1f1" },
    };
  }, [series, locale, seriesMetric]);

  const seriesChartData = useMemo(() => {
    const points = series?.series ?? [];
    if (seriesMetric === "engagement") {
      return [
        {
          name: t("dashboard.series.opened"),
          data: points.map((p) => p.opened),
        },
        {
          name: t("dashboard.series.clicked"),
          data: points.map((p) => p.clicked),
        },
      ];
    }
    if (seriesMetric === "goals") {
      return [
        {
          name: t("dashboard.series.goals"),
          data: points.map((p) => p.goal_count),
        },
      ];
    }
    return [
      {
        name: t("dashboard.series.sent"),
        data: points.map((p) => p.sent),
      },
      {
        name: t("dashboard.series.bounced"),
        data: points.map((p) => p.bounced),
      },
    ];
  }, [series, t, seriesMetric]);

  const hasUsageVolume = useMemo(
    () => (usageSeries?.series ?? []).some((p) => p.used > 0),
    [usageSeries]
  );

  const usageChartOptions = useMemo((): ApexOptions => {
    const categories = (usageSeries?.series ?? []).map((p) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short",
        }).format(new Date(p.date));
      } catch {
        return p.date;
      }
    });
    return {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 2, columnWidth: "60%" } },
      dataLabels: { enabled: false },
      colors: ["#b2d608"],
      xaxis: { categories },
      yaxis: {
        labels: {
          formatter: (v: number) => Math.round(v).toLocaleString(locale),
        },
      },
      grid: { borderColor: "#f1f1f1" },
      tooltip: {
        y: {
          formatter: (val: number, opts?: { dataPointIndex?: number }) => {
            const idx = opts?.dataPointIndex ?? 0;
            const point = usageSeries?.series?.[idx];
            if (!point) {
              return Number(val).toLocaleString(locale);
            }
            return t("dashboard.usage.tooltip", {
              used: point.used.toLocaleString(locale),
              campaign: point.campaign_sent.toLocaleString(locale),
              api: point.api_sent.toLocaleString(locale),
            });
          },
        },
      },
    };
  }, [usageSeries, locale, t]);

  const usageChartData = useMemo(
    () => [
      {
        name: t("dashboard.usage.seriesName"),
        data: (usageSeries?.series ?? []).map((p) => p.used),
      },
    ],
    [usageSeries, t]
  );

  // Sparkline na cota — tendência sem ocupar viewport (só se há volume).
  const quotaSparkOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: "area",
        sparkline: { enabled: true },
        toolbar: { show: false },
      },
      stroke: { curve: "smooth", width: 2 },
      fill: { opacity: 0.25 },
      colors: ["#b2d608"],
      tooltip: { enabled: false },
    }),
    []
  );

  const quotaSparkSeries = useMemo(
    () => [
      {
        name: t("dashboard.usage.sparkline"),
        data: (usageSeries?.series ?? []).map((p) => p.used),
      },
    ],
    [usageSeries, t]
  );

  const exportSeriesCsv = () => {
    const points = series?.series ?? [];
    if (points.length === 0) {
      return;
    }
    const csv = seriesToCsv(
      points.map((p) => ({
        date: p.date,
        sent: p.sent,
        bounced: p.bounced,
        opened: p.opened,
        clicked: p.clicked,
        goal_count: p.goal_count,
      })),
      ["date", "sent", "bounced", "opened", "clicked", "goal_count"]
    );
    downloadTextFile(`sends-series-${seriesDays}d.csv`, csv);
  };

  const exportSeriesPng = async () => {
    try {
      const chart = seriesChartRef.current;
      if (!chart || typeof chart.dataURI !== "function") {
        toast.error(t("dashboard.series.exportPngError"));
        return;
      }
      const result = await chart.dataURI();
      const imgURI =
        typeof result === "object" && result && "imgURI" in result
          ? String((result as { imgURI: string }).imgURI)
          : null;
      if (!imgURI) {
        toast.error(t("dashboard.series.exportPngError"));
        return;
      }
      const a = document.createElement("a");
      a.href = imgURI;
      a.download = `sends-series-${seriesDays}d.png`;
      a.click();
    } catch {
      toast.error(t("dashboard.series.exportPngError"));
    }
  };

  const nextToneClass =
    nextStep.tone === "warning"
      ? "border-warning dashboard-next-step dashboard-next-step--warning"
      : nextStep.tone === "info"
        ? "border-info dashboard-next-step dashboard-next-step--info"
        : nextStep.tone === "success"
          ? "border-success dashboard-next-step dashboard-next-step--success"
          : "border-primary dashboard-next-step";

  const nextBtnClass =
    nextStep.tone === "warning"
      ? "btn-warning"
      : nextStep.tone === "info"
        ? "btn-primary"
        : nextStep.tone === "success"
          ? "btn-success"
          : "btn-primary";

  const renderAttentionBody = (item: AttentionItem) => {
    if (item.bodyKey === "dashboard.dailyCap.exhausted") {
      return (
        <Trans
          i18nKey={item.bodyKey}
          values={item.bodyValues}
          components={{ strong: <strong /> }}
        />
      );
    }
    return t(item.bodyKey, item.bodyValues);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("dashboard.title")}
          pageTitle={t("dashboard.home")}
        />
        <ToastContainer closeButton={false} limit={3} />

        {/* LAC-197 / LAC-100: banner billing — past_due (grace) ou soft-lock. */}
        {!quotaLoading && subscription?.status === "past_due" ? (
          <Alert color="warning" className="mb-3">
            <strong className="d-block mb-1">
              {t("dashboard.billing.pastDue.title")}
            </strong>
            <p className="mb-2">{t("dashboard.billing.pastDue.body")}</p>
            {typeof subscription.days_until_suspend === "number" ? (
              <p className="mb-2 fw-medium">
                {subscription.days_until_suspend <= 0
                  ? t("dashboard.billing.pastDue.countdownToday")
                  : t("dashboard.billing.pastDue.countdown", {
                      days: subscription.days_until_suspend,
                    })}
              </p>
            ) : null}
            <Link
              to="/settings/subscription?focus=payment"
              className="btn btn-sm btn-warning"
            >
              {t("dashboard.billing.pastDue.cta")}
            </Link>
          </Alert>
        ) : null}
        {!quotaLoading && subscription?.status === "suspended" ? (
          <Alert color="danger" className="mb-3">
            <strong className="d-block mb-1">
              {t("dashboard.billing.suspended.title")}
            </strong>
            <p className="mb-2">{t("dashboard.billing.suspended.body")}</p>
            <Link
              to="/settings/subscription?focus=payment"
              className="btn btn-sm btn-danger"
            >
              {t("dashboard.billing.suspended.cta")}
            </Link>
          </Alert>
        ) : null}
        {!quotaLoading && subscription?.status === "canceled" ? (
          <Alert color="danger" className="mb-3">
            <strong className="d-block mb-1">
              {t("dashboard.billing.canceled.title")}
            </strong>
            <p className="mb-2">{t("dashboard.billing.canceled.body")}</p>
            <Link
              to="/settings/subscription?focus=payment"
              className="btn btn-sm btn-danger"
            >
              {t("dashboard.billing.canceled.cta")}
            </Link>
          </Alert>
        ) : null}

        {bootLoading ? (
          <Card className="mb-3">
            <CardBody className="text-center py-5">
              <Spinner color="primary" className="mb-3" />
              <p className="text-muted mb-0">{t("dashboard.boot.loading")}</p>
              <div className="placeholder-glow mt-4 mx-auto" style={{ maxWidth: 480 }}>
                <span className="placeholder col-12 mb-2" />
                <span className="placeholder col-10 mb-2" />
                <span className="placeholder col-8" />
              </div>
            </CardBody>
          </Card>
        ) : (
          <>
        {/* Próxima ação — primeiro viewport: o que precisa de mim agora. */}
        <Card className={`mb-3 border-start border-3 ${nextToneClass}`}>
          <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="flex-grow-1">
              <p className="text-muted text-uppercase fw-medium small mb-1">
                {t("dashboard.nextStep.label")}
              </p>
              <h4 className="mb-1">{t(nextStep.titleKey)}</h4>
              <p className="text-muted mb-0">
                {nextStep.bodyKey === "dashboard.dailyCap.exhausted" ? (
                  <Trans
                    i18nKey={nextStep.bodyKey}
                    values={nextStep.bodyValues}
                    components={{ strong: <strong /> }}
                  />
                ) : (
                  t(nextStep.bodyKey, nextStep.bodyValues)
                )}
              </p>
              <p className="small text-muted mb-0 mt-2">
                {t("dashboard.greeting", { name: tenantName })}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Link to={nextStep.to} className={`btn ${nextBtnClass}`}>
                {t(nextStep.ctaKey)}
              </Link>
              {nextStep.secondaryCtaKey && nextStep.secondaryTo ? (
                <Link
                  to={nextStep.secondaryTo}
                  className="btn btn-soft-secondary"
                >
                  {t(nextStep.secondaryCtaKey)}
                </Link>
              ) : null}
              {lastCampaignEditId ? (
                <Link
                  to={`/campaigns/${lastCampaignEditId}/edit`}
                  className="btn btn-primary"
                >
                  {t("dashboard.prefs.continueEdit")}
                </Link>
              ) : null}
            </div>
          </CardBody>
        </Card>

        {attentionItems.length > 0 && (
          <Card
            className={`mb-3 dashboard-attention-card${
              attentionItems.some((i) => i.tone === "danger")
                ? " dashboard-attention-card--danger"
                : ""
            }`}
          >
            <CardBody className="py-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0">{t("dashboard.attention.title")}</h5>
                <span className="badge bg-secondary-subtle text-secondary">
                  {t("dashboard.attention.count", {
                    count: attentionItems.length,
                  })}
                </span>
              </div>
              <ul className="list-unstyled mb-0">
                {attentionItems.map((item, idx) => (
                  <li
                    key={item.id}
                    className={`d-flex flex-wrap justify-content-between align-items-start gap-2 py-2${
                      idx < attentionItems.length - 1
                        ? " border-bottom border-dashed"
                        : ""
                    }`}
                  >
                    <div className="flex-grow-1">
                      <div className="fw-medium">
                        <span
                          className={`badge me-2 bg-${item.tone}-subtle text-${item.tone}`}
                        >
                          {t(`dashboard.attention.tone.${item.tone}`)}
                        </span>
                        {t(item.titleKey)}
                      </div>
                      <div className="small text-muted mt-1">
                        {renderAttentionBody(item)}
                      </div>
                      {item.id === "inactive" && inactiveSummary ? (
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                          <label
                            className="small text-muted mb-0"
                            htmlFor="dash-inactive-threshold"
                          >
                            {t("dashboard.threshold.label")}
                          </label>
                          <Input
                            id="dash-inactive-threshold"
                            type="select"
                            bsSize="sm"
                            style={{ width: "auto" }}
                            disabled={thresholdSaving}
                            value={
                              inactiveSummary.dashboard_threshold_percent ?? 20
                            }
                            onChange={(e) =>
                              void saveThreshold(Number(e.target.value))
                            }
                          >
                            {THRESHOLD_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {t("dashboard.threshold.option", {
                                  percent: opt,
                                })}
                              </option>
                            ))}
                          </Input>
                        </div>
                      ) : null}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <Link
                        to={item.to}
                        className={`btn btn-sm btn-soft-${item.tone}`}
                      >
                        {t(item.ctaKey)}
                      </Link>
                      {item.dismissible ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost-secondary"
                          onClick={() => dismissAttention(item)}
                          aria-label={t("dashboard.attention.dismiss")}
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* Resumo 7d — digest leve sem e-mail. */}
        <Card className="mb-3">
          <CardBody className="py-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h5 className="mb-0">{t("dashboard.digest.title")}</h5>
                {isHotList ? (
                  <span className="badge bg-success-subtle text-success">
                    {t("dashboard.digest.hotList")}
                  </span>
                ) : null}
              </div>
              <Link to="/sends" className="btn btn-sm btn-primary">
                {t("dashboard.digest.cta")}
              </Link>
            </div>
            <Row className="g-2">
              <Col sm={6} md={3}>
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">
                    {t("dashboard.digest.sent")}
                  </div>
                  <div className="fs-5 fw-semibold">
                    {(stats?.sent ?? 0).toLocaleString(locale)}
                  </div>
                  {weekCompare.hasCompare &&
                  weekCompare.sentDeltaPct != null ? (
                    <div
                      className={`small mt-1 ${
                        weekCompare.sentDeltaPct >= 0
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {t("dashboard.digest.compareSent", {
                        delta:
                          (weekCompare.sentDeltaPct >= 0 ? "+" : "") +
                          weekCompare.sentDeltaPct,
                      })}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col sm={6} md={3}>
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">
                    {t("dashboard.digest.openRate")}
                  </div>
                  <div className="fs-5 fw-semibold">
                    {hasWeekVolume
                      ? formatRatePercent(stats?.open_rate, locale)
                      : "—"}
                  </div>
                  {weekCompare.hasCompare &&
                  weekCompare.openRatePp != null ? (
                    <div
                      className={`small mt-1 ${
                        weekCompare.openRatePp >= 0
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {t("dashboard.digest.compareOpen", {
                        delta:
                          (weekCompare.openRatePp >= 0 ? "+" : "") +
                          weekCompare.openRatePp,
                      })}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col sm={6} md={3}>
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">
                    {t("dashboard.digest.quota")}
                  </div>
                  <div className="fs-5 fw-semibold">{percent}%</div>
                </div>
              </Col>
              <Col sm={6} md={3}>
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">
                    {t("dashboard.digest.pending")}
                  </div>
                  <div className="fs-5 fw-semibold">
                    {pendingApprovalCount > 0 ? (
                      <Link to="/sends?pending=mine">
                        {pendingApprovalCount}
                      </Link>
                    ) : (
                      0
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            {isOwner() && inactiveSummary ? (
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3 pt-2 border-top">
                <label
                  className="small text-muted mb-0"
                  htmlFor="dash-digest-threshold"
                >
                  {t("dashboard.threshold.label")}
                </label>
                <Input
                  id="dash-digest-threshold"
                  type="select"
                  bsSize="sm"
                  style={{ width: "auto" }}
                  disabled={thresholdSaving}
                  value={inactiveSummary.dashboard_threshold_percent ?? 20}
                  onChange={(e) => void saveThreshold(Number(e.target.value))}
                >
                  {THRESHOLD_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {t("dashboard.threshold.option", { percent: opt })}
                    </option>
                  ))}
                </Input>
                <span className="small text-muted">
                  {t("dashboard.threshold.hint")}
                </span>
              </div>
            ) : null}
          </CardBody>
        </Card>

        {bestHours?.best_hour != null && bestHours.best_hour_local_hint ? (
          <Alert color="info" className="mb-3">
            <strong className="d-block mb-1">
              {t("dashboard.bestHour.title")}
            </strong>
            {t("dashboard.bestHour.body", {
              hint: bestHours.best_hour_local_hint,
            })}
          </Alert>
        ) : null}

        {/* Saúde: cota + ramp no primeiro scroll — “posso enviar?” */}
        <Row className="g-3 mb-3">
          <Col lg={showDailyCap || rampLoading ? 6 : 12} md={12}>
            <Card className="mb-0 h-100">
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h5 className="card-title mb-0">
                    {t("dashboard.quota.title")}
                  </h5>
                  {setup && !setupLoading ? (
                    <span
                      className={`badge ${
                        setup.is_ready
                          ? "bg-success-subtle text-success"
                          : "bg-warning-subtle text-warning"
                      }`}
                    >
                      {setup.is_ready
                        ? t("dashboard.health.setupReady")
                        : t("dashboard.health.setupPending")}
                    </span>
                  ) : null}
                </div>

                {quotaLoading && (
                  <div className="placeholder-glow" aria-hidden="true">
                    <span className="placeholder col-10 mb-2" />
                    <span className="placeholder col-12 mb-2" />
                    <span className="placeholder col-6" />
                  </div>
                )}

                {!quotaLoading && quotaUnavailable && (
                  <div className="text-center py-2">
                    <p className="text-muted mb-2">
                      {t("dashboard.quota.empty.title")}
                    </p>
                    <Link
                      to="/settings/subscription"
                      className="btn btn-sm btn-light"
                    >
                      {t("dashboard.quota.viewPlan")}
                    </Link>
                  </div>
                )}

                {!quotaLoading && subscription && quota > 0 && (
                  <>
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                      <div style={{ width: 120, minWidth: 100 }}>
                        <SafeApexChart
                          options={quotaDonutOptions}
                          series={quotaDonutSeries}
                          type="donut"
                          height={120}
                        />
                      </div>
                      <div className="flex-grow-1">
                        <p className="mb-2">
                          {t("dashboard.quota.usage", {
                            used: used.toLocaleString(i18n.language),
                            quota: quota.toLocaleString(i18n.language),
                          })}
                        </p>
                        <Progress
                          value={percent}
                          color={progressColor}
                          className="progress-sm mb-2"
                        />
                        <div className="d-flex justify-content-between small text-muted">
                          <span>
                            {t("dashboard.quota.remaining", {
                              remaining: remaining.toLocaleString(
                                i18n.language
                              ),
                            })}
                          </span>
                          <span>{percent}%</span>
                        </div>
                      </div>
                    </div>
                    {subscription.plan?.name ? (
                      <p className="small text-muted mb-1">
                        {t("dashboard.quota.plan", {
                          plan: subscription.plan.name,
                        })}
                        {periodEndLabel
                          ? ` · ${t("dashboard.quota.periodEnds", {
                              date: periodEndLabel,
                            })}`
                          : null}
                      </p>
                    ) : null}
                    {hasUsageVolume ? (
                      <div className="mb-2" aria-hidden="true">
                        <SafeApexChart
                          options={quotaSparkOptions}
                          series={quotaSparkSeries}
                          type="area"
                          height={60}
                        />
                      </div>
                    ) : null}
                    {exhausted ? (
                      <Alert color="danger" className="mb-2 py-2">
                        {t("dashboard.quota.exhausted")}
                      </Alert>
                    ) : null}
                    {!exhausted && nearLimit ? (
                      <Alert color="warning" className="mb-2 py-2">
                        {t("dashboard.quota.nearLimit")}
                      </Alert>
                    ) : null}
                    {canBuyPack ? (
                      <Link
                        to="/settings/subscription?buyPack=1"
                        className="btn btn-sm btn-success w-100 mb-2"
                      >
                        {t("dashboard.quota.packCta", {
                          emails: QUOTA_PACK_EMAILS.toLocaleString(locale),
                          price: formatCents(
                            QUOTA_PACK_PRICE_CENTS,
                            "BRL",
                            locale
                          ),
                        })}
                      </Link>
                    ) : null}
                    {packsExhausted ? (
                      <Alert color="info" className="mb-2 py-2 small">
                        {t("dashboard.quota.packsExhausted")}
                      </Alert>
                    ) : null}
                    <Link
                      to={
                        packsExhausted
                          ? "/settings/subscription#upgrade-plans"
                          : "/settings/subscription"
                      }
                      className={`btn btn-sm w-100 ${
                        exhausted || nearLimit
                          ? "btn-warning"
                          : "btn-primary"
                      }`}
                    >
                      {exhausted || nearLimit || packsExhausted
                        ? t("dashboard.quota.upgradeCta")
                        : t("dashboard.quota.viewPlan")}
                    </Link>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          {(rampLoading || showDailyCap) && (
            <Col lg={6} md={12}>
              <Card className="mb-0 h-100">
                <CardBody>
                  <h5 className="card-title mb-3">
                    {t("dashboard.dailyCap.title")}
                  </h5>
                  {rampLoading ? (
                    <div className="placeholder-glow" aria-hidden="true">
                      <span className="placeholder col-10 mb-2" />
                      <span className="placeholder col-12 mb-2" />
                      <span className="placeholder col-6" />
                    </div>
                  ) : showDailyCap ? (
                    <>
                      <p className="mb-2">
                        {t("dashboard.dailyCap.usage", {
                          sent: dailySent.toLocaleString(i18n.language),
                          cap: dailyCap.toLocaleString(i18n.language),
                        })}
                      </p>
                      <Progress
                        value={dailyPercent}
                        color={dailyProgressColor}
                        className="progress-sm mb-2"
                      />
                      <div className="d-flex justify-content-between small text-muted mb-2">
                        <span>
                          {t("dashboard.dailyCap.remaining", {
                            remaining: dailyRemaining.toLocaleString(
                              i18n.language
                            ),
                          })}
                        </span>
                        <span>{dailyPercent}%</span>
                      </div>
                      <p className="small text-muted mb-0">
                        {t("dashboard.dailyCap.hint", {
                          resetAt: dailyCapResetLocal,
                        })}
                      </p>
                    </>
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {isOwner() && (usageLoading || usageSeries) ? (
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="card-title mb-0">{t("dashboard.usage.title")}</h5>
                <span className="text-muted small">
                  {t("dashboard.usage.hint")}
                </span>
              </div>
              {usageLoading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" size="sm" />
                </div>
              ) : !hasUsageVolume ? (
                <Alert color="info" className="mb-0">
                  {t("dashboard.usage.empty")}
                </Alert>
              ) : (
                <>
                  <SafeApexChart
                    dir="ltr"
                    type="bar"
                    height={220}
                    options={usageChartOptions}
                    series={usageChartData}
                    className="apex-charts"
                  />
                  {usageSeries?.source ? (
                    <p className="small text-muted mb-0 mt-2">
                      {usageSeries.source === "ledger"
                        ? t("dashboard.usage.sourceLedger")
                        : t("dashboard.usage.sourceReconstructed")}
                    </p>
                  ) : null}
                </>
              )}
            </CardBody>
          </Card>
        ) : null}

        {/* KPIs 7d — no mobile começam recolhidos. */}
        <div className="mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h5 className="mb-0">{t("dashboard.weekStats.title")}</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted small d-none d-md-inline">
                {t("dashboard.weekStats.periodHint")}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-soft-secondary d-md-none"
                onClick={() => setWeekStatsOpen((o) => !o)}
                aria-expanded={weekStatsOpen}
              >
                {weekStatsOpen
                  ? t("dashboard.weekStats.collapse")
                  : t("dashboard.weekStats.expand")}
              </button>
            </div>
          </div>
          <div className="d-none d-md-block">
            {statsError ? (
              <Alert color="danger" className="mb-0">
                {t("dashboard.weekStats.error")}{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => loadStats()}
                >
                  {t("dashboard.weekStats.retry")}
                </button>
              </Alert>
            ) : !hasWeekVolume ? (
              <Alert color="info" className="mb-0">
                {t("dashboard.weekStats.empty")}
              </Alert>
            ) : (
              <>
                <Row className="g-3">
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.sent")}
                      value={(stats?.sent ?? 0).toLocaleString(locale)}
                      icon="ri-mail-send-line"
                      hint={t("dashboard.weekStats.sendsCount", {
                        count: stats?.sends_count ?? 0,
                      })}
                    />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.openRate")}
                      value={formatRatePercent(stats?.open_rate, locale)}
                      icon="ri-mail-open-line"
                    />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.clickRate")}
                      value={formatRatePercent(stats?.click_rate, locale)}
                      icon="ri-cursor-line"
                    />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.deliveryRate")}
                      value={formatRatePercent(stats?.delivery_rate, locale)}
                      icon="ri-checkbox-circle-line"
                    />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.bounceRate")}
                      value={formatRatePercent(stats?.bounce_rate, locale)}
                      icon="ri-error-warning-line"
                      tone={
                        hardBounceRateHigh || bounceRateHigh
                          ? "danger"
                          : "default"
                      }
                      hint={
                        hardBounceRateHigh
                          ? t("dashboard.weekStats.bounceKpiHintHard")
                          : bounceRateHigh
                            ? t("dashboard.weekStats.bounceKpiHint")
                            : undefined
                      }
                      sparkSeries={bounceSparkSeries}
                    />
                  </Col>
                  <Col xl={2} md={4} sm={6}>
                    <StatKpiCard
                      label={t("dashboard.weekStats.complaintRate")}
                      value={formatRatePercent(stats?.complaint_rate, locale)}
                      icon="ri-alarm-warning-line"
                    />
                  </Col>
                </Row>
              </>
            )}
          </div>
          <Collapse isOpen={weekStatsOpen} className="d-md-none">
            {statsError ? (
              <Alert color="danger" className="mb-0">
                {t("dashboard.weekStats.error")}{" "}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 align-baseline"
                  onClick={() => loadStats()}
                >
                  {t("dashboard.weekStats.retry")}
                </button>
              </Alert>
            ) : !hasWeekVolume ? (
              <Alert color="info" className="mb-0">
                {t("dashboard.weekStats.empty")}
              </Alert>
            ) : (
              <Row className="g-3">
                <Col sm={6}>
                  <StatKpiCard
                    label={t("dashboard.weekStats.sent")}
                    value={(stats?.sent ?? 0).toLocaleString(locale)}
                    icon="ri-mail-send-line"
                  />
                </Col>
                <Col sm={6}>
                  <StatKpiCard
                    label={t("dashboard.weekStats.openRate")}
                    value={formatRatePercent(stats?.open_rate, locale)}
                    icon="ri-mail-open-line"
                  />
                </Col>
                <Col sm={6}>
                  <StatKpiCard
                    label={t("dashboard.weekStats.clickRate")}
                    value={formatRatePercent(stats?.click_rate, locale)}
                    icon="ri-cursor-line"
                  />
                </Col>
                <Col sm={6}>
                  <StatKpiCard
                    label={t("dashboard.weekStats.bounceRate")}
                    value={formatRatePercent(stats?.bounce_rate, locale)}
                    icon="ri-error-warning-line"
                    tone={
                      hardBounceRateHigh || bounceRateHigh
                        ? "danger"
                        : "default"
                    }
                    sparkSeries={bounceSparkSeries}
                  />
                </Col>
              </Row>
            )}
          </Collapse>
        </div>


        {/* Série: volume ou engajamento (opened/clicked já vêm da API). */}
        <div className="mb-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h5 className="mb-0">
              {t("dashboard.series.titleDays", { days: seriesDays })}
            </h5>
            <div className="d-flex flex-wrap gap-2">
              <div
                className="btn-group btn-group-sm"
                role="group"
                aria-label={t("dashboard.series.metricLabel")}
              >
                <button
                  type="button"
                  className={dashSegBtn(seriesMetric === "volume")}
                  onClick={() => persistSeriesMetric("volume")}
                >
                  {t("dashboard.series.metric.volume")}
                </button>
                <button
                  type="button"
                  className={dashSegBtn(seriesMetric === "engagement")}
                  onClick={() => persistSeriesMetric("engagement")}
                >
                  {t("dashboard.series.metric.engagement")}
                </button>
                <button
                  type="button"
                  className={dashSegBtn(seriesMetric === "goals")}
                  onClick={() => persistSeriesMetric("goals")}
                >
                  {t("dashboard.series.metric.goals")}
                </button>
              </div>
              <div
                className="btn-group btn-group-sm"
                role="group"
                aria-label={t("dashboard.series.rangeLabel")}
              >
                <button
                  type="button"
                  className={dashSegBtn(seriesDays === 7)}
                  onClick={() => setSeriesDays(7)}
                >
                  {t("dashboard.series.days7")}
                </button>
                <button
                  type="button"
                  className={dashSegBtn(seriesDays === 30)}
                  onClick={() => setSeriesDays(30)}
                >
                  {t("dashboard.series.days30")}
                </button>
              </div>
              {hasSeriesVolume && !seriesLoading && !seriesError ? (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={exportSeriesCsv}
                  >
                    {t("dashboard.series.exportCsv")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => void exportSeriesPng()}
                  >
                    {t("dashboard.series.exportPng")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          {seriesLoading ? (
            <Card className="mb-0">
              <CardBody className="text-center py-5">
                <Spinner color="primary" size="sm" />
              </CardBody>
            </Card>
          ) : seriesError ? (
            <Alert color="danger" className="mb-0">
              {t("dashboard.weekStats.error")}{" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => loadSeries()}
              >
                {t("dashboard.weekStats.retry")}
              </button>
            </Alert>
          ) : !hasSeriesVolume ? (
            <Alert color="info" className="mb-0">
              {seriesMetric === "engagement"
                ? t("dashboard.series.emptyEngagement")
                : seriesMetric === "goals"
                  ? t("dashboard.series.emptyGoals")
                  : t("dashboard.series.empty")}
            </Alert>
          ) : (
            <Card className="mb-0">
              <CardBody>
                <SafeApexChart
                  chartRef={seriesChartRef}
                  dir="ltr"
                  type="line"
                  height={280}
                  options={seriesChartOptions}
                  series={seriesChartData}
                  className="apex-charts"
                />
              </CardBody>
            </Card>
          )}
        </div>

        <Row className="g-3 mb-3">
          <Col lg={discoveryCards.length > 0 ? 8 : 12}>
            <Card className="mb-0">
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h5 className="card-title mb-0">
                    {t("dashboard.recentSends.title")}
                  </h5>
                  <Link to="/sends" className="btn btn-sm btn-primary">
                    {t("dashboard.recentSends.viewAll")}
                  </Link>
                </div>
                {sendsLoading ? (
                  <div className="placeholder-glow">
                    <span className="placeholder col-12 mb-2" />
                    <span className="placeholder col-10" />
                  </div>
                ) : recentSends.length === 0 ? (
                  <div className="text-center py-4">
                    <div className="avatar-lg mx-auto mb-3">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle display-5">
                        <i className="ri-mail-send-line" />
                      </span>
                    </div>
                    <h5 className="mb-2">{t("dashboard.recentSends.emptyTitle")}</h5>
                    <p className="text-muted mb-3">
                      {t("dashboard.recentSends.empty")}
                    </p>
                    <Link to="/campaigns" className="btn btn-sm btn-primary">
                      {t("dashboard.recentSends.cta")}
                    </Link>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("dashboard.recentSends.campaign")}</th>
                          <th>{t("dashboard.recentSends.status")}</th>
                          <th>{t("dashboard.recentSends.sent")}</th>
                          <th>{t("dashboard.recentSends.openRate")}</th>
                          <th>{t("dashboard.recentSends.clickRate")}</th>
                          <th>{t("dashboard.recentSends.when")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {recentSends.map((item) => {
                          const when =
                            formatRelativePast(
                              item.finished_at ||
                                item.started_at ||
                                item.created_at,
                              locale
                            ) || "—";
                          const needsAction =
                            item.status === "paused" ||
                            item.status === "pending_approval";
                          const bounceHigh = isBounceRateHigh(
                            item.bounce_rate,
                            bounceWarnThreshold
                          );
                          const openHref = bounceHigh
                            ? `/sends/${item.id}?status=bounced`
                            : `/sends/${item.id}`;
                          return (
                            <tr key={item.id}>
                              <td className="fw-medium">
                                {item.campaign?.name || item.subject}
                                {item.is_test ? (
                                  <span className="badge bg-secondary-subtle text-secondary ms-2">
                                    {t("sending.badge.test")}
                                  </span>
                                ) : null}
                                {bounceHigh ? (
                                  <span className="badge bg-danger-subtle text-danger ms-2">
                                    {t("dashboard.recentSends.bounceBadge", {
                                      rate: formatRatePercent(
                                        item.bounce_rate,
                                        locale
                                      ),
                                    })}
                                  </span>
                                ) : null}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    item.status === "paused" &&
                                    item.needs_sa_resume
                                      ? "bg-danger-subtle text-danger"
                                      : sendStatusBadgeClass(item.status)
                                  }`}
                                >
                                  {t(`sending.status.${item.status}`)}
                                </span>
                                {hasCircuitAutoResumeEta(item) ? (
                                  <div className="small text-muted mt-1">
                                    {formatCircuitAutoResumeCountdown(
                                      item.circuit_auto_resume_eta_at,
                                      item.needs_sa_resume,
                                      etaNowMs,
                                      t,
                                      "list"
                                    )}
                                  </div>
                                ) : item.status === "paused" &&
                                  (item.pause_reason === "circuit" ||
                                    item.pause_reason === "platform") &&
                                  item.needs_sa_resume ? (
                                  <div className="small text-danger mt-1">
                                    {t("sending.list.circuitPaused.needsSa")}
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                {item.sent}/{item.queued}
                              </td>
                              <td>
                                {item.sent > 0
                                  ? formatRatePercent(item.open_rate, locale)
                                  : "—"}
                              </td>
                              <td>
                                {item.sent > 0
                                  ? formatRatePercent(item.click_rate, locale)
                                  : "—"}
                              </td>
                              <td className="text-muted small">{when}</td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end flex-wrap gap-1">
                                  {item.status === "pending_approval" &&
                                  canApproveSend() ? (
                                    <>
                                      <Button
                                        color="soft-success"
                                        size="sm"
                                        disabled={approvalBusyId !== null}
                                        onClick={() =>
                                          void approveSendInline(item)
                                        }
                                      >
                                        {approvalBusyId === item.id ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          t("sending.approval.approve")
                                        )}
                                      </Button>
                                      <Button
                                        color="soft-danger"
                                        size="sm"
                                        disabled={approvalBusyId !== null}
                                        onClick={() => {
                                          setRejectTarget(item);
                                          setRejectionReason("");
                                        }}
                                      >
                                        {t("sending.approval.reject")}
                                      </Button>
                                    </>
                                  ) : null}
                                  <Link
                                    to={openHref}
                                    className={`btn btn-sm ${
                                      needsAction &&
                                      item.status !== "pending_approval"
                                        ? "btn-warning"
                                        : "btn-primary"
                                    }`}
                                  >
                                    {needsAction &&
                                    item.status !== "pending_approval"
                                      ? t("dashboard.recentSends.action")
                                      : bounceHigh
                                        ? t("dashboard.recentSends.openBounced")
                                        : t("dashboard.recentSends.open")}
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {discoveryCards.length > 0 ? (
            <Col lg={4}>
              <Card className="mb-0 h-100">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <h5 className="card-title mb-0">
                      {t("dashboard.discover.title")}
                    </h5>
                    <div className="form-check form-check-sm mb-0">
                      <Input
                        id="dash-hide-discovery"
                        type="checkbox"
                        className="form-check-input"
                        checked={prefs.hideDiscovery}
                        onChange={(e) =>
                          persistHideDiscovery(e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label small text-muted"
                        htmlFor="dash-hide-discovery"
                      >
                        {t("dashboard.prefs.hideDiscovery")}
                      </label>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {discoveryCards.map((card) => (
                      <div
                        key={card.id}
                        className="border rounded p-3 bg-light bg-opacity-50"
                      >
                        <div className="d-flex align-items-start gap-2">
                          <span className="avatar-xs flex-shrink-0">
                            <span className="avatar-title bg-primary-subtle text-primary rounded-circle">
                              <i className={card.icon} />
                            </span>
                          </span>
                          <div>
                            <div className="fw-medium mb-1">
                              {t(card.titleKey)}
                            </div>
                            <p className="small text-muted mb-2">
                              {t(card.bodyKey)}
                            </p>
                            <Link
                              to={card.to}
                              className="btn btn-sm btn-primary"
                            >
                              {t(card.ctaKey)}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ) : null}
        </Row>

        {/* Atalhos enxutos + BYO */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h5 className="card-title mb-0">
                {t("dashboard.shortcuts.title")}
              </h5>
              {prefs.hideDiscovery ? (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => persistHideDiscovery(false)}
                >
                  {t("dashboard.prefs.showDiscovery")}
                </button>
              ) : null}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {lastCampaignEditId ? (
                <Link
                  to={`/campaigns/${lastCampaignEditId}/edit`}
                  className="btn btn-primary btn-sm"
                >
                  {t("dashboard.prefs.continueEdit")}
                </Link>
              ) : null}
              <Link to="/contacts" className="btn btn-primary btn-sm">
                {t("dashboard.cta.contacts")}
              </Link>
              <Link to="/campaigns" className="btn btn-primary btn-sm">
                {t("dashboard.cta.campaigns")}
              </Link>
              <Link to="/sends" className="btn btn-primary btn-sm">
                {t("dashboard.cta.sends")}
              </Link>
              <Link to="/tags" className="btn btn-light btn-sm">
                {t("dashboard.cta.tags")}
              </Link>
              <Link
                to="/settings/sending"
                className="btn btn-light btn-sm"
              >
                {t("dashboard.cta.sending")}
              </Link>
              <Link
                to="/settings/subscription"
                className="btn btn-light btn-sm"
              >
                {t("dashboard.cta.subscription")}
              </Link>
            </div>
            {byoCircuit?.available ? (
              <Alert
                color={byoCircuit.near_aws_ceiling ? "warning" : "primary"}
                className="mt-3 mb-0"
              >
                <strong className="d-block mb-1">
                  {t("dashboard.byoCircuit.title")}
                </strong>
                <span className="small">
                  {t("dashboard.byoCircuit.body", {
                    bounce: byoCircuit.effective.bounce_pct,
                    complaint: byoCircuit.effective.complaint_pct,
                    min: byoCircuit.effective.min_sent,
                  })}
                </span>
                <div className="mt-2">
                  <Link
                    to="/settings/sending-controls"
                    className="btn btn-sm btn-primary"
                  >
                    {t("dashboard.byoCircuit.cta")}
                  </Link>
                </div>
              </Alert>
            ) : null}
          </CardBody>
        </Card>
          </>
        )}

        <DashboardTour open={tourOpen} onClose={() => setTourOpen(false)} />

        <SendApprovalRejectModal
          isOpen={rejectTarget != null}
          busy={approvalBusyId !== null}
          reason={rejectionReason}
          onReasonChange={setRejectionReason}
          onClose={() => {
            if (approvalBusyId !== null) return;
            setRejectTarget(null);
            setRejectionReason("");
          }}
          onConfirm={() => void confirmRejectInline()}
        />
      </Container>
    </div>
  );
};

export default TenantHome;
